import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Mic, MicOff, Video as VideoIcon, VideoOff, PhoneOff } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCall } from "../context/CallContext";

const ICE_CONFIG = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
};

const Call = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { sendSignal, registerHandler, incomingCall, setIncomingCall } = useCall();

  const isCallee = location.state?.role === "callee";
  const incomingRef = useRef(isCallee ? incomingCall : null);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const pcRef = useRef(null);
  const peerIdRef = useRef(null);
  const pendingIceRef = useRef([]);
  const remoteSetRef = useRef(false);
  const endedRef = useRef(false);
  const connectedRef = useRef(false);

  const [peer, setPeer] = useState(null);
  const [status, setStatus] = useState("connecting"); // connecting | ringing | connected | unavailable
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);
  const [seconds, setSeconds] = useState(0);

  const cleanup = useCallback(() => {
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    try { pcRef.current?.close(); } catch (e) {}
    pcRef.current = null;
  }, []);

  const endCall = useCallback((notifyPeer = true) => {
    if (endedRef.current) return;
    endedRef.current = true;
    if (notifyPeer && peerIdRef.current) sendSignal({ type: "end", to: peerIdRef.current });
    cleanup();
    navigate(`/chat/${matchId}`);
  }, [cleanup, matchId, navigate, sendSignal]);

  const drainIce = useCallback(async () => {
    for (const c of pendingIceRef.current) {
      try { await pcRef.current?.addIceCandidate(c); } catch (e) {}
    }
    pendingIceRef.current = [];
  }, []);

  const createPeerConnection = useCallback(() => {
    const pc = new RTCPeerConnection(ICE_CONFIG);
    pc.onicecandidate = (e) => {
      if (e.candidate && peerIdRef.current) {
        sendSignal({ type: "ice", to: peerIdRef.current, candidate: e.candidate });
      }
    };
    pc.ontrack = (e) => {
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = e.streams[0];
      connectedRef.current = true;
      setStatus("connected");
    };
    pc.onconnectionstatechange = () => {
      if (connectedRef.current && ["failed", "disconnected", "closed"].includes(pc.connectionState)) {
        endCall(false);
      }
    };
    pcRef.current = pc;
    return pc;
  }, [sendSignal, endCall]);

  // Signaling handler
  useEffect(() => {
    const unregister = registerHandler(async (data) => {
      if (data.from && peerIdRef.current && data.from !== peerIdRef.current) return;
      const pc = pcRef.current;
      if (data.type === "answer" && pc) {
        try {
          await pc.setRemoteDescription(data.sdp);
          remoteSetRef.current = true;
          await drainIce();
        } catch (e) {}
      } else if (data.type === "ice" && pc) {
        if (remoteSetRef.current) {
          try { await pc.addIceCandidate(data.candidate); } catch (e) {}
        } else {
          pendingIceRef.current.push(data.candidate);
        }
      } else if (data.type === "unavailable") {
        setStatus("unavailable");
      } else if (data.type === "reject") {
        toast("Appel refusé.");
        endCall(false);
      } else if (data.type === "end") {
        toast("Appel terminé.");
        endCall(false);
      }
    });
    return unregister;
  }, [registerHandler, drainIce, endCall]);

  // Main setup
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Determine peer identity
      let peerInfo = null;
      if (isCallee && incomingRef.current) {
        peerInfo = { user_id: incomingRef.current.from, name: incomingRef.current.caller_name, photo_url: incomingRef.current.caller_photo };
      } else {
        try {
          const { data } = await api.get("/matches");
          const m = data.find((x) => x.match_id === matchId);
          if (m) peerInfo = m.profile;
        } catch (e) {}
      }
      if (cancelled) return;
      if (!peerInfo) { toast.error("Match introuvable."); navigate(`/chat/${matchId}`); return; }
      peerIdRef.current = peerInfo.user_id;
      setPeer(peerInfo);

      // Get local media (fallback to audio-only if the camera is unavailable)
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      } catch (e) {
        if (e?.name === "NotAllowedError" || e?.name === "SecurityError") {
          toast.error("Autorisation refusée. Appuyez sur l'icône 🔒 (ou ⓘ) dans la barre d'adresse et autorisez la caméra et le micro pour KoraLove, puis réessayez.", { duration: 8000 });
          navigate(`/chat/${matchId}`);
          return;
        }
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
          toast("Caméra indisponible — appel audio uniquement 🎙️", { duration: 6000 });
        } catch (e2) {
          if (e2?.name === "NotFoundError" || e2?.name === "DevicesNotFoundError") {
            toast.error("Aucune caméra ni micro détecté sur cet appareil.", { duration: 6000 });
          } else if (e2?.name === "NotReadableError" || e2?.name === "TrackStartError") {
            toast.error("Caméra/micro déjà utilisé par une autre application. Fermez-la puis réessayez.", { duration: 6000 });
          } else {
            toast.error(`Caméra/micro indisponible (${e2?.name || "erreur"}). Vérifiez les autorisations.`, { duration: 6000 });
          }
          navigate(`/chat/${matchId}`);
          return;
        }
      }
      if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
      localStreamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;

      const pc = createPeerConnection();

      if (isCallee && incomingRef.current) {
        // Callee: setRemoteDescription(offer) FIRST (fires ontrack synchronously),
        // then addTrack, then createAnswer.
        try {
          await pc.setRemoteDescription(incomingRef.current.sdp);
          remoteSetRef.current = true;
          await drainIce();
          localStreamRef.current.getTracks().forEach((t) => pc.addTrack(t, localStreamRef.current));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          sendSignal({ type: "answer", to: peerIdRef.current, sdp: { type: pc.localDescription.type, sdp: pc.localDescription.sdp } });
          // Note: do NOT setStatus("connecting") here — ontrack fires synchronously
          // during setRemoteDescription(offer) and already set status to "connected".
        } catch (e) {
          toast.error("Erreur lors de la connexion.");
        }
        setIncomingCall(null);
      } else {
        // Caller: addTrack, then create and send offer
        localStreamRef.current.getTracks().forEach((t) => pc.addTrack(t, localStreamRef.current));
        const offer = await pc.createOffer({ offerToReceiveVideo: true, offerToReceiveAudio: true });
        await pc.setLocalDescription(offer);
        const delivered = sendSignal({
          type: "offer", to: peerIdRef.current, sdp: { type: pc.localDescription.type, sdp: pc.localDescription.sdp }, match_id: matchId,
          caller_name: user?.name, caller_photo: user?.photo_url || user?.picture,
        });
        if (!delivered) { setStatus("unavailable"); return; }
        setStatus("ringing");
        setTimeout(() => {
          setStatus((s) => (s === "ringing" ? "unavailable" : s));
        }, 20000);
      }
    })();

    return () => {
      cancelled = true;
      cleanup();
    };
    // eslint-disable-next-line
  }, [matchId]);

  useEffect(() => {
    if (status !== "connected") return;
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [status]);

  const toggleMute = () => {
    const audio = localStreamRef.current?.getAudioTracks?.()[0];
    if (audio) { audio.enabled = muted; setMuted(!muted); }
  };
  const toggleCam = () => {
    const video = localStreamRef.current?.getVideoTracks?.()[0];
    if (video) { video.enabled = camOff; setCamOff(!camOff); }
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const statusLabel = {
    connecting: "Connexion…",
    ringing: "Appel en cours…",
    connected: fmt(seconds),
    unavailable: "Utilisateur indisponible",
  }[status];

  return (
    <div className="min-h-screen bg-kora-ink relative overflow-hidden" data-testid="call-screen">
      {/* Remote video / photo placeholder */}
      <div className="absolute inset-0">
        <video ref={remoteVideoRef} autoPlay playsInline className={`w-full h-full object-cover ${status === "connected" ? "" : "hidden"}`} data-testid="remote-video" />
        {status !== "connected" && peer && (
          <img src={resolveImg(peer.photo_url)} alt={peer.name} className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-kora-ink/40 via-transparent to-kora-ink/80" />
      </div>

      {/* Header */}
      <div className="relative pt-10 text-center text-white">
        <p className="font-display font-bold text-2xl" data-testid="call-peer-name">{peer?.name || "Appel"}</p>
        <p className="text-white/70 text-sm mt-1" data-testid="call-status">{statusLabel}</p>
        {status === "unavailable" && (
          <p className="text-white/60 text-xs mt-2 max-w-xs mx-auto px-6">
            {peer?.name} n'est pas connecté(e) pour le moment. Réessayez quand vous êtes tous les deux en ligne.
          </p>
        )}
      </div>

      {/* Local preview */}
      <div className="absolute top-6 right-4 w-28 h-40 rounded-2xl overflow-hidden border-2 border-white/40 shadow-lg bg-black">
        <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="local-video" />
        {camOff && <div className="absolute inset-0 bg-kora-ink flex items-center justify-center"><VideoOff size={22} className="text-white/60" /></div>}
      </div>

      {/* Controls */}
      <div className="absolute bottom-10 left-0 w-full flex justify-center items-center gap-5">
        <button data-testid="call-mute" onClick={toggleMute} className={`w-14 h-14 rounded-full flex items-center justify-center backdrop-blur-md transition-colors ${muted ? "bg-white text-kora-ink" : "bg-white/20 text-white"}`}>
          {muted ? <MicOff size={24} /> : <Mic size={24} />}
        </button>
        <button data-testid="call-end" onClick={() => endCall(true)} className="w-16 h-16 rounded-full bg-red-500 text-white flex items-center justify-center shadow-lg">
          <PhoneOff size={28} />
        </button>
        <button data-testid="call-camera" onClick={toggleCam} className={`w-14 h-14 rounded-full flex items-center justify-center backdrop-blur-md transition-colors ${camOff ? "bg-white text-kora-ink" : "bg-white/20 text-white"}`}>
          {camOff ? <VideoOff size={24} /> : <VideoIcon size={24} />}
        </button>
      </div>
    </div>
  );
};

export default Call;
